import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useLocalizedStrings } from "../Localization";
import { ConfigurationContext } from "../Context";
import { usePost } from "../API";
import { safeParse, SpacedSpinner } from "../Util";
import { Card, CardHeader, CardBody, CardFooter, Button, Icon, CodeDisplay, DateDisplay } from "@aidkitorg/component-library";
import { HistoryItem } from "@aidkitorg/aidkit/lib/application/history";
import { AppInfoMetadata } from "@aidkitorg/types/lib/survey";
import { getPrettyValue, useDebouncedSearch, HistorySearchBar, HistoryFilterOptions, HistoryListWrapper, filterEmptyErrors } from "./historyUtils";

type HistoryItemProps = {
  item: HistoryItem;
  config: Record<string, any>;
  searchTerm: string;
};

function HistoryItemComponent(props: HistoryItemProps) {
  const L = useLocalizedStrings();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const popParentRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; right: number } | null>(null);

  function highlightText(text?: string, searchTerm?: string): JSX.Element {
    if (!text) return <></>;
    if (!searchTerm) return <>{text}</>;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? <span key={index} className="bg-yellow-100">{part}</span> : part
        )}
      </>
    );
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.metadata-popover') && !target.closest('.popover-button')) {
        setPopoverVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (popoverVisible && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      
      // Calculate position relative to viewport (fixed positioning)
      const popoverWidth = 288; // w-72 = 18rem = 288px
      const spacing = 10;
      
      // Position to the left of the button
      let right = window.innerWidth - buttonRect.right;
      let top = buttonRect.top - 25; // 25px above the button
      
      // Adjust if popover would go off screen
      if (right + popoverWidth > window.innerWidth) {
        right = window.innerWidth - popoverWidth - spacing;
      }
      if (right < spacing) {
        right = spacing;
      }
      
      // Adjust if popover would go off top of screen
      if (top < spacing) {
        top = buttonRect.bottom + spacing;
      }
      
      setPopoverPosition({ top, right });
    } else {
      setPopoverPosition(null);
    }
  }, [popoverVisible]);

  const { pretty: prettyValue, isJSON } = useMemo(() => getPrettyValue(props.item.value), [props.item.value]);

  const parsedMetadata: AppInfoMetadata = useMemo(() => {
    return safeParse(props.item.metadata);
  }, [props.item.metadata]);

  // A cross-app contact update names its origin app in `source`. Show it as a link — the raw
  // `updatedAcrossAppsFrom:<uid>` wraps onto a second line, and the uid is only useful to follow.
  const propagatedFrom = useMemo(() => {
    const prefix = 'updatedAcrossAppsFrom:';
    return parsedMetadata?.source?.startsWith(prefix) ? parsedMetadata.source.slice(prefix.length) : undefined;
  }, [parsedMetadata?.source]);

  const formattedMetadata = useMemo(() => {
    if (!props.item.metadata) return '';
    try {
      const parsed = JSON.parse(props.item.metadata);
      // If there's a log field, replace literal \n sequences with actual newlines
      if (parsed && typeof parsed === 'object' && 'log' in parsed && typeof parsed.log === 'string') {
        // Replace literal backslash-n (the two characters \ and n) with actual newline
        const logWithNewlines = parsed.log.replace(/\\n/g, '\n');
        const formatted = { ...parsed, log: logWithNewlines };
        // Stringify the object - this will escape the newlines as \n in the JSON string
        let stringified = JSON.stringify(formatted, null, 2);
        // Replace \n (escaped newline in JSON string) with actual newlines in the log field
        // In the stringified JSON, a newline is represented as the characters \ followed by n
        stringified = stringified.replace(/"log":\s*"([^"]*(?:\\.[^"]*)*)"/g, (match, content) => {
          // Replace \n (backslash-n, 2 characters) with actual newline character
          // In regex, \ needs to be escaped, so \\n matches the literal \n
          const withNewlines = content.replace(/\\n/g, '\n');
          return `"log": "${withNewlines}"`;
        });
        return stringified;
      }
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If it's not valid JSON, return as-is but try to replace \n with actual newlines
      return props.item.metadata.replace(/\\n/g, '\n');
    }
  }, [props.item.metadata]);

  return (
    <Card className="p-3">
      <CardHeader
        title={<strong>{highlightText(props.item.key, props.searchTerm)}</strong>}
        badgeText={!!props.item.latest ? L.applicant.history.latest : undefined}
        badgeVariant="success"
        actionButton={
          <Button
            ref={buttonRef}
            aria-label="Show metadata"
            className="popover-button px-0"
            variant="unstyled"
            icon={<Icon
              name="InformationCircleIcon"
              role="decorative"
            />}
            onClick={() => { setPopoverVisible(!popoverVisible) }}
          >
          </Button>
        }
      />
      <CardBody className="py-2">
        <>
          {props.config?.roles?.includes('admin') && popoverVisible && popoverPosition && createPortal(
            <div 
              ref={popoverRef} 
              className="fixed z-50 p-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg metadata-popover"
              style={{
                top: `${popoverPosition.top}px`,
                right: `${popoverPosition.right}px`
              }}
            >
              <div className="p-2 text-sm font-medium text-gray-700">
                {L.campaigns.metadata}
              </div>
              <pre className="bg-gray-100 p-2 max-h-80 overflow-y-auto text-sm text-gray-700 text-monospace whitespace-pre-wrap">
                {formattedMetadata}
              </pre>
            </div>,
            document.body
          )}
          <div>
            <CodeDisplay
              className='border-0'
              variant="gray"
              contentMode="wrap"
              language={isJSON ? "json" : "text"}
              code={prettyValue}
              highlightText={props.searchTerm}
              copyable />
          </div>
        </>
      </CardBody>
      <CardFooter>
        <div className="text-sm flex justify-end flex-col items-end">
          <div>
            {propagatedFrom
              ? <span><Link to={`/a/${propagatedFrom}`} target="_blank" title={propagatedFrom}
                className="text-indigo-600 hover:text-indigo-900">updated from another app</Link>{' - '}</span>
              : parsedMetadata?.source && <span>{parsedMetadata.source + ' - '}</span>}
            {highlightText(props.item.author_name, props.searchTerm)}
          </div>
          <div>
            <DateDisplay variant='datetime' utcValue={props.item.created_at} />
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

type HistoryProps = { uid: string, canViewChanges: boolean };

export default function History(props: HistoryProps) {
  const L = useLocalizedStrings();
  const [searchInput, setSearchInput] = useState('');
  const searchTerm = useDebouncedSearch(searchInput, 200);
  const [exactMatch, setExactMatch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isUpdating = useRef(false);
  const config = useContext(ConfigurationContext);
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [searchTargetField, setSearchTargetField] = useState(true);
  const [searchValue, setSearchValue] = useState(true);
  const [searchAuthor, setSearchAuthor] = useState(true);
  const [onlyShowLatest, setOnlyShowLatest] = useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState(true);
  const [hideEmptyErrors, setHideEmptyErrors] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const getHistory = usePost('/applicant/get_history');

  type NextParams = NonNullable<(Awaited<ReturnType<typeof getHistory>>['nextParams'])>;

  const [nextParams, setNextParams] = useState<NextParams | undefined>();
  const [history, setHistory] = useState<Map<number, HistoryItem>>(new Map());
  const [filteredHistory, setFilteredHistory] = useState<HistoryItem[]>([]);

  const [useUTC, setUseUTC] = useState(false);
  const onCheckedChange = useCallback((checked: boolean) => setUseUTC(checked), []);

  const showMoreRef = useRef(null);

  const searchHistory = useCallback(async (options?: { nextParams?: typeof nextParams, offset?: number, limit?: number }) => {
    isUpdating.current = true;
    setIsLoading(true);
    const fetchedHistory = await getHistory(options?.nextParams ?? {
      applicant: props.uid,
      substring_search_key: searchTargetField ? searchTerm.trim() : undefined,
      substring_search_author: searchAuthor ? searchTerm.trim() : undefined,
      substring_search_value: searchValue ? searchTerm.trim() : undefined,
      exact: exactMatch,
      latest: onlyShowLatest ? true : undefined,
      hide_empty_values: !showEmptyFields,
      start_date: startDate ? new Date(startDate).toISOString() : undefined,
      end_date: endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() : undefined,
      offset: options?.offset,
      limit: options?.limit
    });
    if (fetchedHistory && !('error' in fetchedHistory)) {
      if (options?.nextParams) {
        setHistory(
          history => {
            for (const item of fetchedHistory.changes) {
              history.set(item.id, item);
            }  
            return history;    
          }
        )
      }
      else {
        setHistory(new Map(fetchedHistory.changes.map(item => [item.id, item])));
      }
      setNextParams(fetchedHistory.nextParams);
    }
    setIsLoading(false);  
    isUpdating.current = false;
  }, [props.uid, searchTargetField, searchAuthor, searchValue, searchTerm, exactMatch, onlyShowLatest, showEmptyFields, startDate, endDate]);

  // refetch on all other changes
  useEffect(() => {
    searchHistory();
  }, [searchHistory]);

  // polling interval
  useEffect(() => {
    const pollingInterval = setInterval(
      () => {
        searchHistory({ offset: 0, limit: Math.max(history.size, 50) });
      },
      5000
    );
    return () => {
      clearInterval(pollingInterval);
    }
  }, [searchHistory, history.size]) 

  // When empty values are shown, hide always-empty errors on the client side.
  // (Empty values themselves are filtered server-side via hide_empty_values.)
  useEffect(() => {
    if (history) {
      const incomingHistory = Array.from(history.values());
      const filtered = filterEmptyErrors(incomingHistory, incomingHistory, hideEmptyErrors);
      setFilteredHistory(filtered);
    }
  }, [history, history.size, hideEmptyErrors]);

  useEffect(() => {
    // Show more ref to render more results when a user scrolls to the bottom of the list.
    // debounce this event
    let debounceTimeout : NodeJS.Timeout | undefined;
    const observer = new IntersectionObserver((entries) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        debounceTimeout = undefined;
      }
      debounceTimeout = setTimeout(
        () => {
          entries.forEach(entry => {            
            if (entry.isIntersecting && entry.target === showMoreRef.current && history.size && nextParams) {
              searchHistory({ nextParams });
              observer.unobserve(showMoreRef.current);
            }
          });    
        },
        200
      );
    });

    if (showMoreRef.current) {
      observer.observe(showMoreRef.current);
    }

    return () => {
      if (showMoreRef.current) {
        observer.unobserve(showMoreRef.current);
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [nextParams, showMoreRef.current, history.size]);

  return (
    <div>
      <HistorySearchBar
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        searchTerm={searchTerm}
        showSearchOptions={showSearchOptions}
        setShowSearchOptions={setShowSearchOptions}
        L={L}
      />
      {showSearchOptions && (
        <HistoryFilterOptions
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          exactMatch={exactMatch}
          setExactMatch={setExactMatch}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          searchTargetField={searchTargetField}
          setSearchTargetField={setSearchTargetField}
          searchAuthor={searchAuthor}
          setSearchAuthor={setSearchAuthor}
          onlyShowLatest={onlyShowLatest}
          setOnlyShowLatest={setOnlyShowLatest}
          showEmptyFields={showEmptyFields}
          setShowEmptyFields={setShowEmptyFields}
          hideEmptyErrors={hideEmptyErrors}
          setHideEmptyErrors={setHideEmptyErrors}
          L={L}
        />
      )}
      <HistoryListWrapper
        useUTC={useUTC}
        onCheckedChange={onCheckedChange}
        footer={
          <>
            {(nextParams && isLoading) && <div className="flex justify-center">
              <SpacedSpinner className="text-gray-500" />
            </div>}
            { (nextParams && history.size > 0) && <div ref={showMoreRef} /> }
          </>
        }
      >
        {filteredHistory.map((item: HistoryItem, i: number) => (
          <HistoryItemComponent key={`history_${item.id}_${i}`} item={item} config={config} searchTerm={searchTerm} />
        ))}
      </HistoryListWrapper>
    </div>
  );
}
